import React from "react";

import { ErrorScreen } from "@tm/ui/ui-container";
import { pdtLog } from "@tm/utility/analytics";

interface IErrorBoundaryState {
  hasError: boolean;
  error?: any;
}

interface IErrorBoundaryProps {
  onError?: (err: string) => JSX.Element;
  children?: JSX.Element;
}

export class ErrorBoundary extends React.Component<
  IErrorBoundaryProps,
  IErrorBoundaryState
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: any) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    try {
      if (typeof window !== "undefined") {
        const page_name =
          typeof window?.location?.pathname === "string"
            ? window.location.pathname
            : "unknown";
        const payload = {
          message: error?.message ?? String(error),
          name: error?.name ?? "Error",
          stack: error?.stack ?? "",
          componentStack: errorInfo?.componentStack ?? "",
        };
        pdtLog({
          event_name: "error_boundary_caught",
          event_value: JSON.stringify(payload),
          page_name,
          event_id: `error_boundary:${page_name}:${payload.message}`,
        });
      }
    } catch {
      // never throw from error boundary
    }
    console.error("componentDidCatch", error);
  }

  override render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI

      const error = this.state.error.getErrorMessage
        ? this.state.error.getErrorMessage()
        : this.state.error;
      return this.props.onError?.(error) || <ErrorScreen error={error} />;
    }

    return this.props.children;
  }
}
